import * as THREE from 'three';
import { roadMapBridge } from '../render/roadMapBridge';

// cityGround — how high the ground is, wherever an artist stands.
//
// The city model's ground is NOT at y = 0. Measured 2026-10-23: the asphalt is
// at 0.00, every pavement and open lot at 0.086, the Corner Market's forecourt
// at 0.11. Bodies placed at 0 stood ankle-deep in the pavement, and the first
// plaza floor laid at 0.03 vanished under the lot it was built on.
//
// The road map (render/roadMap) already says, per texel, what kind of surface a
// spot is — asphalt, pavement/lot, court (`surf`). So the height of a point is
// the height of ITS KIND of surface, and those few heights are measured once,
// from the model itself, by casting rays straight down on texels of each kind
// and taking the median. Nothing hand-typed to go stale when the city is
// re-exported; one array read per lookup, so it is free to ask every frame.
//
// DECKS are the floors this World lays on top (components/city/CityPlaces: the
// plaza's stone, the park's grass). Inside one, you stand on the deck.

const ASPHALT = 1; const PAVEMENT = 2; const COURT = 3;
// Until measured: today's model, as measured. Replaced by the real numbers
// within seconds of the city loading.
const levels = { [ASPHALT]: 0, [PAVEMENT]: 0.086, [COURT]: 0.086 };
let measured = false;
const decks = new Map();   // id → { rect: [minX, maxX, minZ, maxZ], at: [x, z], lift }
const subs = new Set();

function surfAt(x, z) {
  const map = roadMapBridge.map;
  const surf = map?.surf;
  if (!surf) return PAVEMENT;
  const { w: W, h: H, rect } = map;
  const u = map.unitsPerTexel || rect.w / W;
  const i = Math.floor((x - rect.minX) / u); const r = Math.floor((z - rect.minZ) / u);
  if (i < 0 || r < 0 || i >= W || r >= H) return PAVEMENT;
  const s = surf[r * W + i];
  // Covered (0) is a doorway, an awning, a prop's footprint: whoever is there
  // is standing on the pavement under it.
  return s === ASPHALT || s === COURT ? s : PAVEMENT;
}

/** The city's own ground at (x, z), ignoring the decks laid over it. */
export function baseGroundY(x, z) {
  return levels[surfAt(x, z)] ?? levels[PAVEMENT];
}

/** Where a foot rests at (x, z): a deck if there is one here, else the ground. */
export function groundY(x, z) {
  for (const d of decks.values()) {
    const [a, b, c, e] = d.rect;
    if (x >= a && x <= b && z >= c && z <= e) return baseGroundY(d.at[0], d.at[1]) + d.lift;
  }
  return baseGroundY(x, z);
}

/** A floor laid over the ground (the plaza, the park). */
export function addDeck(id, rect, lift) {
  const [a, b, c, e] = rect;
  decks.set(id, { rect, at: [(a + b) / 2, (c + e) / 2], lift });
}
export function removeDeck(id) { decks.delete(id); }

export const groundMeasured = () => measured;
export function onGround(fn) { subs.add(fn); return () => subs.delete(fn); }

const DOWN = new THREE.Vector3(0, -1, 0);
const ray = new THREE.Raycaster();
const origin = new THREE.Vector3();
const under = (o, roots) => { for (let p = o; p; p = p.parent) if (roots.includes(p)) return true; return false; };

/** One downward ray from just above the feet, ignoring `skip` subtrees. */
function probe(scene, x, z, skip) {
  // From a step above the pavement, not from the sky: a ray from the sky stops
  // on canopies, hoops and awnings and calls them the ground.
  origin.set(x, 0.6, z);
  ray.set(origin, DOWN);
  ray.far = 1.6;
  let hits = [];
  try { hits = ray.intersectObjects(scene.children, true); } catch { return null; }
  const h = hits.find((q) => q.object?.isMesh && !q.object.isSprite && !q.object.isSkinnedMesh && q.object.visible && !under(q.object, skip));
  return h ? h.point.y : null;
}

const median = (a) => { const s = [...a].sort((p, q) => p - q); return s[Math.floor(s.length / 2)]; };

/**
 * Measure the height of each kind of surface. Call until it returns true (the
 * road map and the city both have to have arrived). `skip` = this World's own
 * additions, which must not be mistaken for the ground they sit on.
 */
export function measureGround(scene, skip = []) {
  if (measured) return true;
  const map = roadMapBridge.map;
  if (!map?.surf || !scene) return false;
  const { surf, w: W, h: H, rect } = map;
  const u = map.unitsPerTexel || rect.w / W;
  const found = { [ASPHALT]: [], [PAVEMENT]: [], [COURT]: [] };
  // A coarse, fixed stride through the map: the same texels every visit.
  const stride = Math.max(1, Math.floor((W * H) / 2400));
  for (let i = Math.floor(stride / 2); i < W * H; i += stride) {
    const s = surf[i];
    if (!found[s] || found[s].length >= 40) continue;
    const x = rect.minX + ((i % W) + 0.5) * u;
    const z = rect.minZ + (Math.floor(i / W) + 0.5) * u;
    const y = probe(scene, x, z, skip);
    if (y != null) found[s].push(y);
  }
  if (found[ASPHALT].length < 8 || found[PAVEMENT].length < 8) return false;
  levels[ASPHALT] = median(found[ASPHALT]);
  levels[PAVEMENT] = median(found[PAVEMENT]);
  levels[COURT] = found[COURT].length >= 5 ? median(found[COURT]) : levels[PAVEMENT];
  measured = true;
  console.log(`[city] ground measured: asphalt ${levels[ASPHALT].toFixed(3)} · pavement ${levels[PAVEMENT].toFixed(3)} · court ${levels[COURT].toFixed(3)}`);
  subs.forEach((fn) => { try { fn(levels); } catch { /* a reader's problem */ } });
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    window.__cityGround = { levels: { ...levels }, samples: Object.fromEntries(Object.entries(found).map(([k, v]) => [k, v.length])), groundY, probe: (x, z) => probe(scene, x, z, skip) };
  }
  return true;
}
